import React from 'react';

import { FlexCol, FlexRow, TitleSmall } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import { getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { MedExam } from '../../tasks/types/medExam';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

export const MedExamDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const medExam: MedExam = task.taskData as MedExam;
  const evidence = task.evidence;
  const labOptions: string[] = medExam.labsOptions ? medExam.labsOptions : [];
  const evidenceReasons: string[] = medExam.reason.map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const labsDocuments =
    evidence &&
    evidence.documents.filter(document => {
      return document.documentType === DocumentType.MEDEXAM_LABS;
    });

  const pmDocuments =
    evidence &&
    evidence.documents.filter(document => {
      return document.documentType === DocumentType.MEDEXAM_PM;
    });

  const ekgDocuments =
    evidence &&
    evidence.documents.filter(document => {
      return document.documentType === DocumentType.MEDEXAM_EKG;
    });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  return (
    <div data-label={'medExam_section'}>
      <FlexRow id={task.id.toString()} className={styles.Title}>
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
        </FlexCol>
        <TitleSmall>{Resource.TASK_TITLE_MEDEXAM}</TitleSmall>
        <FlexCol className={styles.CreatedDate}>
          {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
        </FlexCol>
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {medExam.labs && labsDocuments && labsDocuments.length > 0 && (
        <FileDetail
          documents={labsDocuments}
          displayString={[Resource.LABS, ...labOptions].join(
            Resource.NORA_SEPARATOR,
          )}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}
      {medExam.labs &&
        task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={
              !(labsDocuments && labsDocuments.length > 0)
                ? [Resource.LABS, ...labOptions].join(Resource.NORA_SEPARATOR)
                : ''
            }
            selectedFiles={onFileSelection}
            type={DocumentType.MEDEXAM_LABS}
            ongoingUpload={task.ongoingUpload?.[DocumentType.MEDEXAM_LABS]}
          />
        )}
      {medExam.physicalMeasurements &&
        pmDocuments &&
        pmDocuments.length > 0 && (
          <FileDetail
            documents={pmDocuments}
            displayString={Resource.PHYSICAL_MEASUREMENT}
            fileOpenHandler={downloadHandler}
            fileDeletehandler={(documentId: number, reason: string) =>
              deleteHandler(task, documentId, reason)
            }
          />
        )}
      {medExam.physicalMeasurements &&
        task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={
              !(pmDocuments && pmDocuments.length > 0)
                ? Resource.PHYSICAL_MEASUREMENT
                : ''
            }
            selectedFiles={onFileSelection}
            type={DocumentType.MEDEXAM_PM}
            ongoingUpload={task.ongoingUpload?.[DocumentType.MEDEXAM_PM]}
          />
        )}
      {medExam.ekg && ekgDocuments && ekgDocuments.length > 0 && (
        <FileDetail
          documents={ekgDocuments}
          displayString={Resource.EKG}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}
      {medExam.ekg &&
        task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={
              !(ekgDocuments && ekgDocuments.length > 0) ? Resource.EKG : ''
            }
            selectedFiles={onFileSelection}
            type={DocumentType.MEDEXAM_EKG}
            ongoingUpload={task.ongoingUpload?.[DocumentType.MEDEXAM_EKG]}
          />
        )}
      {task.state !== TaskState.REQUESTED && (
        <TaskNote taskID={task.id} originalNote={medExam?.note} />
      )}
      {(evidenceReasons?.length > 0 || medExam?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {medExam?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={medExam.reasonDescription}
            />
          )}
        </>
      )}
    </div>
  );
};
